import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link, useNavigate } from "react-router-dom";
import { useInView } from 'react-intersection-observer';
import axios from 'axios';
import { FaYoutube } from "react-icons/fa";
import {
  CalendarIcon,
  ClockIcon,
  ArrowRightIcon,
} from '@heroicons/react/24/outline';
import { getImageUrl } from '../../utils/helpers';

const BlogSection = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [videoData, setVideoData] = useState({
    title: 'How to Apply for AHAM Home Loan in 5 Easy Steps',
    views: '2.5K',
    description: 'Watch our step-by-step guide on how to apply for a home loan with AHAM Housing Finance. Learn about documentation, eligibility, and the approval process.',
    thumbnail: '/img/Blogs/Loan2.webp',
    link: 'https://www.youtube.com/@ahamhfc' // Default/Fallback link
  });

  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchBlogData = async () => {
      try {
        setLoading(true);
        const response = await axios.get('/api/blog/active');
        if (response.data.success) {
          setBlogPosts(response.data.data.slice(0, 3));
        }
      } catch (error) {
        console.error('Error fetching blog data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchBlogData();
  }, []);

  useEffect(() => {
    const fetchVideoData = async () => {
      try {
        const response = await axios.get('/api/admin/blog/featured-video');
        if (response.data.success && response.data.data) {
          setVideoData(response.data.data);
        }
      } catch (error) {
        console.error('Error fetching featured video:', error);
      }
    };
    fetchVideoData();
  }, []);


  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 15
      }
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="container mx-auto px-4">
        <motion.div
          ref={ref}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={containerVariants}
          className="text-center mb-16"
        >
          <motion.h2
            variants={itemVariants}
            className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
          >
            {t('blog.title')}
          </motion.h2>
          <motion.p
            variants={itemVariants}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            {t('blog.subtitle')}
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-7xl mx-auto">
          {/* Blog Posts */}
          <motion.div
            variants={containerVariants}
            className="space-y-8"
          >
            {blogPosts.map((post, index) => (
              <motion.article
                key={post.id}
                variants={itemVariants}
                onClick={() => navigate(`/home-loan-blog-aham-housing-finance/${post.id}`)}
                className="group cursor-pointer block"
                whileHover={{ y: -5 }}
                transition={{ type: 'spring', stiffness: 300 }}
              >
                <div className="bg-white rounded-2xl shadow-soft hover:shadow-large transition-all duration-500 overflow-hidden">
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    <div className="md:w-1/3 h-48 md:h-auto relative overflow-hidden">
                      <div
                        className="absolute inset-0 bg-cover bg-center bg-no-repeat transform group-hover:scale-110 transition-transform duration-700"
                       style={{ backgroundImage: `url("${getImageUrl(post.image_url)}")` }}
                       role="img"
                        aria-label={post.title}
/>
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/20 to-transparent" />

                      {/* Category Badge */}
                      <div className="absolute top-4 left-4">
                        <span className="bg-white/90 backdrop-blur-sm text-primary-600 px-3 py-1 rounded-full text-xs font-semibold">
                          {post.category}
                        </span>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="md:w-2/3 p-6">
                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-3">
                        <div className="flex items-center space-x-1">
                          <CalendarIcon className="h-4 w-4" />
                          <span>{new Date(post.date).toLocaleDateString()}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <ClockIcon className="h-4 w-4" />
                          <span>{post.read_time}</span>
                        </div>
                      </div>

                      <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {post.title}
                      </h3>

                      <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                        {post.excerpt}
                      </p>

                      <button className="flex items-center space-x-2 text-primary-600 font-semibold group-hover:text-primary-700 transition-colors">
                        <span>Read More</span>
                        <ArrowRightIcon className="h-4 w-4 transform group-hover:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.article>
            ))}
          </motion.div>

          {/* YouTube Video & Newsletter */}
          <motion.div
            variants={containerVariants}
            className="space-y-8"
          >
            {/* Featured Video */}
            <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-soft overflow-hidden"
            >
              <div className="relative">
                <div className="aspect-video bg-gradient-to-br from-primary-600 to-purple-600 relative overflow-hidden">
                  <div
                    className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-50"
                    style={{
                      backgroundImage: `url("${getImageUrl(videoData.thumbnail)}")`
                    }}
                     role="img"
                    aria-label={`Video Thumbnail: ${videoData.title}`}
                  />

                  <div className="absolute inset-0 flex items-center justify-center">
                    <button
                      onClick={() => window.open(videoData.link, '_blank')}
                      className="group bg-white rounded-full p-4 shadow-lg hover:scale-110 transition-transform duration-300"
                      aria-label="Play Video"
                    >
                      <FaYoutube className="h-12 w-12 text-red-600 group-hover:text-red-700 transition-colors" />
                    </button>
                  </div>

                  <div className="absolute bottom-4 left-4 right-4">
                    <h4 className="text-white font-semibold mb-2 line-clamp-2">
                      {videoData.title}
                    </h4>
                    <div className="flex items-center space-x-4 text-white/80 text-sm">
                      <span>AHAM Housing Finance</span>
                      <span>•</span>
                      <span>{videoData.views} views</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-gray-600 leading-relaxed line-clamp-3">
                  {videoData.description}
                </p>
              </div>
            </motion.div>

            {/* Newsletter Signup */}
            {/* <motion.div
              variants={itemVariants}
              className="bg-gradient-to-br from-primary-50 to-purple-50 rounded-2xl p-8"
            >
              <div className="text-center mb-6">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">
                  Stay Updated
                </h4>
                <p className="text-gray-600">
                  Get the latest home loan news, rates, and tips directly in your inbox.
                </p>
              </div>

              <form className="space-y-4">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-4 focus:ring-primary-200 focus:border-primary-500 transition-all duration-200"
                />
                
                <button
                  type="submit"
                  className="w-full bg-primary-600 hover:bg-primary-700 text-white font-semibold py-3 px-6 rounded-lg transition-colors duration-300"
                >
                  {t('forms.newsletter')}
                </button>
              </form>

              <p className="text-xs text-gray-500 text-center mt-4">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </motion.div> */}

            {/* Quick Links */}
            {/* <motion.div
              variants={itemVariants}
              className="bg-white rounded-2xl shadow-soft p-6"
            >
              <h4 className="text-xl font-bold text-gray-900 mb-4">
                Popular Resources
              </h4>
              
              <div className="space-y-3">
                {[
                  'Home Loan Eligibility Calculator',
                  'Document Checklist',
                  'Interest Rate Comparison',
                  'PMAY 2.0 Application Guide',
                  'EMI vs Interest Rate Chart'
                ].map((link, index) => (
                  <button
                    key={index}
                    className="flex items-center justify-between w-full text-left py-2 px-3 rounded-lg hover:bg-gray-50 transition-colors group"
                  >
                    <span className="text-gray-700 group-hover:text-primary-600">{link}</span>
                    <ArrowRightIcon className="h-4 w-4 text-gray-400 group-hover:text-primary-600 group-hover:translate-x-1 transition-all" />
                  </button>
                ))}
              </div>
            </motion.div> */}
          </motion.div>
        </div>

        <motion.div
          variants={itemVariants}
          className="text-center mt-16"
        >
          <Link
            to="/home-loan-blog-aham-housing-finance"
            className="btn bg-[#ed2636] px-6 py-3 text-white rounded-lg hover:bg-primary-600 hover:text-white transition inline-block"

          >
            View All Articles
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default BlogSection;